import { useState } from 'react'
import { useGetAdminAgentsQuery, useDeleteAgentMutation, useGetAdminCategoriesQuery } from '../../store/api/agentsApi'
import type { AgentAdminRecord } from '../../store/api/agentsApi'
import { AgentFormModal } from './AgentFormModal'
import { Button, EmptyState, StatusPill } from '../../components/ui'
import { AdminRefreshBtn } from './AdminRefreshBtn'
import s from '../../styles/pages/AdminPage.module.css'

export function AdminAgents() {
  const { data, isLoading, isFetching, refetch } = useGetAdminAgentsQuery()
  const { data: categoriesData } = useGetAdminCategoriesQuery()
  const [deleteAgent] = useDeleteAgentMutation()
  const agents = data?.agents ?? []
  const categories = categoriesData?.categories ?? []

  const [agentModal, setAgentModal] = useState<'new' | AgentAdminRecord | null>(null)
  const [search, setSearch] = useState('')

  const filtered = search.trim()
    ? agents.filter(a =>
        a.name.toLowerCase().includes(search.toLowerCase()) ||
        a.model?.toLowerCase().includes(search.toLowerCase()) ||
        a.provider?.toLowerCase().includes(search.toLowerCase())
      )
    : agents

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Deactivate agent "${name}"? It will no longer respond to discussions.`)) return
    await deleteAgent(id).unwrap().catch(() => {})
  }

  return (
    <div>
      <div className={s.sectionHeader}>
        <div className={s.sectionTitle}>AI Agents {!isLoading && `(${agents.length})`}</div>
        <div className={s.sectionActions}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search agents…"
            style={{
              padding: '0.35rem 0.65rem', borderRadius: 8, border: '1px solid var(--border)',
              background: 'var(--bg-elevated)', color: 'var(--text-primary)',
              fontSize: '0.8rem', outline: 'none', width: 200,
            }}
          />
          <AdminRefreshBtn onClick={() => void refetch()} loading={isFetching} />
          <Button size="sm" onClick={() => setAgentModal('new')}>+ New Agent</Button>
        </div>
      </div>

      <div className={s.listCard}>
        {isLoading && <div className={s.listCardLoading}>Loading…</div>}
        {filtered.map((agent) => {
          const agentCats = categories.filter(c => agent.categoryIds?.includes(c.id))
          return (
            <div key={agent.id} className={s.listRow}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: `${(agent as any).color ?? '#7c3aed'}22`, border: `1px solid ${(agent as any).color ?? '#7c3aed'}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', flexShrink: 0 }}>
                {(agent as any).icon ?? '🤖'}
              </div>
              <div className={s.listRowInfo}>
                <div className={s.listRowName}>{agent.name}</div>
                <div className={s.listRowSub}>
                  <span>{agent.provider} · {agent.model}</span>
                  {agentCats.length === 0
                    ? <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>General</span>
                    : agentCats.map(cat => (
                        <span key={cat.id} style={{ background: `${cat.color ?? 'var(--accent-primary)'}22`, color: cat.color ?? 'var(--accent-primary)', padding: '0.05rem 0.4rem', borderRadius: '6px', fontWeight: 600, fontSize: '0.72rem' }}>
                          {cat.icon} {cat.name}
                        </span>
                      ))
                  }
                </div>
              </div>
              <StatusPill variant={agent.status === 'active' ? 'success' : 'warning'}>{agent.status}</StatusPill>
              <Button size="sm" variant="secondary" onClick={() => setAgentModal(agent)}>✏️ Edit</Button>
              <Button size="sm" variant="danger" onClick={() => void handleDelete(agent.id, agent.name)}>Deactivate</Button>
            </div>
          )
        })}
        {!isLoading && filtered.length === 0 && (
          <EmptyState icon="🤖" title={search.trim() ? `No agents match "${search}"` : 'No agents found'} style={{ border: 'none', borderRadius: 0 }} />
        )}
      </div>

      {agentModal && (
        <AgentFormModal
          existing={agentModal === 'new' ? undefined : agentModal as any}
          categories={categories}
          isAdmin
          onClose={() => { setAgentModal(null); void refetch() }}
        />
      )}
    </div>
  )
}
